import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Users, ShieldCheck, BarChart2, Stethoscope, LogOut,
  Bot, Scroll, MessageSquare, KeyRound, ShieldAlert, Activity,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useSDK } from '../../context/SDKContext.tsx';
import type { SystemHealth } from '@assistia/sdk';

type OverallStatus = SystemHealth['overall'];

export default function Sidebar(): React.ReactElement {
  const { logout, role } = useAuth();
  const isAdmin = role === 'admin' || role === 'root';
  const { sdk } = useSDK();
  const [overallStatus, setOverallStatus] = useState<OverallStatus>('healthy');

  // Polling OBS-02 toutes les 30s pour la pastille + badge "Santé système"
  useEffect(() => {
    const fetchHealth = () => {
      sdk.admin.getHealth()
        .then(({ data }) => setOverallStatus(data.overall))
        .catch(() => setOverallStatus('critical'));
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 30_000);
    return () => clearInterval(id);
  }, [sdk]);

  const pastilleColor = {
    healthy:  'bg-green-500 shadow-green-500/50',
    degraded: 'bg-yellow-500 shadow-yellow-500/50',
    critical: 'bg-red-500 shadow-red-500/50',
  }[overallStatus];

  // Badge rouge si dégradé ou critique
  const showHealthBadge = overallStatus !== 'healthy';

  return (
    <aside className="w-60 flex flex-col bg-card border-r border-border h-screen select-none">
      {/* Header avec Pastille Santé */}
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <span className="text-base font-bold text-primary tracking-tight">Assistia Admin</span>
        <div className="flex items-center gap-1.5" title={`Statut : ${healthStatus.toUpperCase()}`}>
          <span className={`w-2.5 h-2.5 rounded-full shadow-sm ${pastilleColor} transition-all duration-500 animate-pulse`} />
        </div>
      </div>

      {/* Navigation scrollable */}
      <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
        {/* Retour à l'espace RAG (PWA) — "/chat" est réservé au futur chat conversationnel (P1) */}
        <NavLink
          to={role === 'user' ? "/chat" : "/rag"}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`
          }
        >
          <MessageSquare size={15} />
          {role === 'user' ? "Chat" : "RAG"}
        </NavLink>

        {/* Section 1: Configuration */}
        {role !== 'user' && (
        <div className="space-y-1">
          <p className="px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
            🔧 Configuration
          </p>
          {isAdmin && (<>
          <NavLink
            to="/users"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <Users size={15} />
            Utilisateurs
          </NavLink>
          <NavLink
            to="/premium"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <ShieldCheck size={15} />
            Premium / Licences
          </NavLink>
          <NavLink
            to="/licenses"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <KeyRound size={15} />
            Licences (clés)
          </NavLink>
          </>)}
        </div>
        )}

        {/* Section 2: Ops */}
        <div className="space-y-1">
          <p className="px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
            📊 Ops
          </p>
          {role !== 'user' && (
          <NavLink
            to="/dossiers"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <Scroll size={15} />
            Dossiers actifs
          </NavLink>
          )}
          {role !== 'user' && role !== 'auditor' && (
          <NavLink
            to="/hitl"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <Bot size={15} />
            Queue HITL
          </NavLink>
          )}
          <NavLink
            to="/clausebenef"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <Scroll size={15} />
            <span className="flex-1">Clause Bénéficiaire</span>
            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/20 scale-90 shrink-0">
              Demo
            </span>
          </NavLink>
        </div>

        {/* Section 3: Plateforme */}
        {role !== 'user' && (
        <div className="space-y-1">
          <p className="px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
            💰 Plateforme
          </p>
          {(role === 'auditor' || isAdmin) && (
          <NavLink
            to="/audit"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <ShieldAlert size={15} />
            Audit
          </NavLink>
          )}
          {role !== 'auditor' && (
          <NavLink
            to="/finops"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <BarChart2 size={15} />
            FinOps
          </NavLink>
          )}
          {role !== 'auditor' && (
          <NavLink
            to="/env-doctor"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <Stethoscope size={15} />
            Env Doctor
          </NavLink>
          )}
          {role !== 'auditor' && (
          <NavLink
            to="/health"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <Activity size={15} />
            <span className="flex-1">Santé système</span>
            {showHealthBadge && (
              <span className="w-2 h-2 rounded-full bg-destructive shadow-sm shadow-destructive/50 shrink-0" />
            )}
          </NavLink>
          )}
          {isAdmin && (
          <>
          <NavLink
            to="/governance"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <ShieldCheck size={15} />
            Gouvernance
          </NavLink>
          <NavLink
            to="/ai-act"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`
            }
          >
            <ShieldCheck size={15} />
            Registre AI Act
          </NavLink>
          </>
          )}
        </div>
        )}
      </nav>

      {/* Footer Déconnexion */}
      <div className="px-3 py-4 border-t border-border">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <LogOut size={15} />
          Déconnexion
        </button>
      </div>
    </aside>
  );
}

